import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBox } from '../context/BoxContext';
import { toast } from 'react-toastify';
import { emailService } from '../services/emailService';
import { orderService } from '../services/orderService';

const VALID_PROMO_CODES = {
  "123": 0.05, // 5% discount
};

const BoxModal = () => {
  const navigate = useNavigate();
  const { 
    boxItems, 
    isBoxOpen, 
    setIsBoxOpen, 
    removeFromBox, 
    updateQuantity, 
    clearBox,
    getTotalPrice 
  } = useBox();

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showOrderForm, setShowOrderForm] = useState(false);

  const [promoCode, setPromoCode] = useState('');
  const [promoCodeError, setPromoCodeError] = useState('');
  const [appliedPromo, setAppliedPromo] = useState({ code: '', discount: 0 });

  const [orderForm, setOrderForm] = useState({
    fullName: '',
    phone: '',
    address: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalItemCount = boxItems.reduce((total, item) => total + item.quantity, 0);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'currency',
      currency: 'MAD'
    }).format(price);
  };

  // Discount calculation
  const getDiscountedTotal = () => {
    const total = getTotalPrice();
    if (appliedPromo.discount) {
      return total - total * appliedPromo.discount;
    }
    return total;
  };

  const handleQuantityChange = (productId, newQuantity) => {
    updateQuantity(productId, parseInt(newQuantity));
  };

  const handleInputChange = (e) => {
    setOrderForm({
      ...orderForm,
      [e.target.name]: e.target.value
    });
  };

  // Promo code validation
  const handleApplyPromoCode = () => {
    const code = promoCode.trim();
    if (code === "") {
      setPromoCodeError('');
      setAppliedPromo({ code: '', discount: 0 });
      return;
    }
    if (VALID_PROMO_CODES[code]) {
      setPromoCodeError('');
      setAppliedPromo({ code, discount: VALID_PROMO_CODES[code] });
    } else {
      setPromoCodeError("Code promo invalide");
      setAppliedPromo({ code: '', discount: 0 });
    }
  };

  // Show review modal when "Passer la commande" is clicked
  const handleShowReviewModal = () => {
    setShowReviewModal(true);
    setShowOrderForm(false);
    setPromoCode('');
    setPromoCodeError('');
    setAppliedPromo({ code: '', discount: 0 });
  };

  // Proceed from review modal to order form
  const handleProceedToOrderForm = () => {
    setShowOrderForm(true);
    setShowReviewModal(false);
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    if (!orderForm.fullName || !orderForm.phone || !orderForm.address) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (boxItems.length === 0) {
      toast.error('Votre boîte est vide');
      return;
    }
    setIsSubmitting(true);

    try {
      const productsList = boxItems.map((item, index) => 
        `${index + 1}. ${item.name} (${item.shortTitle})
     Quantité : ${item.quantity}
     Prix unitaire : ${formatPrice(item.price)}
     Sous-total : ${formatPrice(item.price * item.quantity)}`
      ).join('\n\n');

      const discountedTotal = getDiscountedTotal();

      const orderData = {
        fullName: orderForm.fullName,
        phone: orderForm.phone,
        address: orderForm.address,
        promoCode: appliedPromo.code,
        productName: `Commande Box El Hiba Para (${boxItems.length} produits différents)`,
        productPrice: formatPrice(discountedTotal),
        productsList: productsList,
        totalItems: boxItems.reduce((total, item) => total + item.quantity, 0),
        orderDate: new Date().toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Europe/Paris'
        })
      };

      await emailService.sendOrderEmail(orderData);

      // Save the order so it appears in the admin "Orders" dashboard
      try {
        await orderService.createOrder({
          customerName: orderForm.fullName,
          phone: orderForm.phone,
          address: orderForm.address,
          promoCode: appliedPromo.code || null,
          products: boxItems.map((item) => ({
            name: item.name,
            shortTitle: item.shortTitle || '',
            quantity: item.quantity,
            price: item.price,
            subtotal: item.price * item.quantity
          })),
          totalItems: orderData.totalItems,
          totalPrice: discountedTotal,
          status: 'pending'
        });
      } catch (orderSaveError) {
        console.error('Erreur lors de l\'enregistrement de la commande :', orderSaveError);
      }

      toast.success(`🎉 Commande envoyée avec succès ! Nous vous contacterons au ${orderForm.phone}`);
      clearBox();
      setOrderForm({
        fullName: '',
        phone: '',
        address: ''
      });
      setShowOrderForm(false);
      setIsBoxOpen(false);
    } catch (error) {
      console.error('Erreur lors de la passation de la commande :', error);
      toast.error('Échec de l\'envoi de la commande. Veuillez réessayer ou nous contacter directement.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isBoxOpen) return null;

  // MODAL 1: Cart & Promo Code Review Modal (simple, clean, app.css friendly, mobile responsive)
  if (showReviewModal) {
    return (
      <div className="modal-overlay" onClick={() => setIsBoxOpen(false)}>
        <div className="modal-content large box-modal-review" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setIsBoxOpen(false)}>
            <i className="fas fa-times"></i>
          </button>
          <div className="modal-header">
            <h2>
              <i className="fas fa-cart-shopping"></i>
              Vérification du panier
            </h2>
            <p>Vérifiez vos articles et appliquez un code promo si vous en avez un</p>
          </div>
          {boxItems.length === 0 ? (
            <div className="box-empty">
              <i className="fas fa-box-open"></i>
              <h3>Votre boîte est vide</h3>
              <p>Commencez à ajouter des produits à votre boîte pour passer une commande</p>
              <button 
                onClick={() => { setIsBoxOpen(false); navigate('/products'); }} 
                className="btn btn-primary"
                style={{ marginTop: '1rem' }}
              >
                <i className="fas fa-shopping-bag"></i>
                Continuer mes achats
              </button>
            </div>
          ) : (
            <>
              <div className="box-items-list">
                {boxItems.map(item => (
                  <div key={item.id} className="box-item-row">
                    <img 
                      src={item.imageUrl || 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=80&h=80&fit=crop'} 
                      alt={item.name}
                    />
                    <div className="box-item-info">
                      <div className="box-item-title">{item.name}</div>
                      <div className="box-item-sub">{item.shortTitle}</div>
                      <div className="box-item-unit">{formatPrice(item.price)} l'unité</div>
                    </div>
                    <div className="box-item-actions">
                      <span>Qté :</span>
                      <select
                        value={item.quantity}
                        onChange={e => handleQuantityChange(item.id, e.target.value)}
                      >
                        {[1,2,3,4,5,6,7,8,9,10].map(num => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                      </select>
                      <div className="box-item-total">
                        {formatPrice(item.price * item.quantity)}
                      </div>
                      <button
                        onClick={() => removeFromBox(item.id)}
                        className="box-item-remove"
                        title="Retirer de la boîte"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="box-modal-summary">
                <div className="box-modal-total-row">
                  <h3>
                    Total ({totalItemCount} article{totalItemCount > 1 ? 's' : ''})
                  </h3>
                  <h3 className="box-modal-total">{formatPrice(getDiscountedTotal())}</h3>
                </div>
                {appliedPromo.discount > 0 && (
                  <div className="box-promo-applied">
                    Remise appliquée: -{Math.round(appliedPromo.discount * 100)}%
                  </div>
                )}
              </div>
              <div className="box-modal-footer">
                <div className="box-promo">
                  <label htmlFor="promoCode">
                    <i className="fas fa-tag"></i> Code promo
                  </label>
                  <div className="box-promo-inputs">
                    <input
                      type="text"
                      id="promoCode"
                      name="promoCode"
                      value={promoCode}
                      onChange={e => setPromoCode(e.target.value)}
                      placeholder="Votre code promo"
                      onBlur={handleApplyPromoCode}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleApplyPromoCode(); } }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={handleApplyPromoCode}
                    >
                      Appliquer
                    </button>
                  </div>
                  {promoCodeError && (
                    <div className="box-promo-error">{promoCodeError}</div>
                  )}
                  {appliedPromo.code && appliedPromo.discount > 0 && (
                    <div className="box-promo-success">
                      Code promo accepté : -{Math.round(appliedPromo.discount * 100)}%
                    </div>
                  )}
                </div>
                <div className="box-modal-actions">
                  <button
                    onClick={() => { setShowReviewModal(false); setIsBoxOpen(false); }}
                    className="btn btn-outline"
                  >
                    <i className="fas fa-arrow-left"></i>
                    Annuler
                  </button>
                  <button
                    onClick={handleProceedToOrderForm}
                    className="btn btn-primary"
                    disabled={boxItems.length === 0}
                  >
                    <i className="fas fa-credit-card"></i>
                    Procéder à la commande
                  </button>
                </div>
              </div>
              <style>{`
                .box-modal-review, .box-modal-summary, .box-modal-footer, .box-items-list, .box-item-row, .box-item-info, .box-item-actions, .box-item-remove {
                  box-sizing: border-box;
                }
                .box-items-list { margin-bottom: 1.5rem; }
                .box-item-row { display: flex; align-items: center; gap: 1rem; padding: .9rem .2rem; border-bottom: 1px solid #f3f4f6; }
                .box-item-row img { width: 52px; height: 52px; border-radius: 8px; object-fit: cover; border: 1px solid #eee; background: #fff; }
                .box-item-info { flex: 1; min-width: 0; }
                .box-item-title { font-weight: 600; font-size: 1.01rem; color: #23232b; }
                .box-item-sub { color: #023A1B; font-size: 0.93rem;}
                .box-item-unit { color: #667eea; font-weight: 500; font-size: .97rem; }
                .box-item-actions { display: flex; align-items: center; gap: .5rem; }
                .box-item-actions select { padding: 0.2rem 0.7rem; border-radius: 5px; border: 1px solid #d1d5db; font-size: 1rem; }
                .box-item-total { font-weight: 700; min-width: 64px; text-align: right; color: #23232b; }
                .box-item-remove { background: none; border: none; color: #ef4444; cursor: pointer; padding: 8px; border-radius: 5px; }
                .box-item-remove:hover { background: #fee2e2; }
                .box-modal-summary { border-top: 1px solid #eee; padding: 0.9rem 0 0.7rem 0; background: #fafafa; }
                .box-modal-total-row { display: flex; justify-content: space-between; align-items: center; font-size: 1.2rem; font-weight: 600; color: #23232b;}
                .box-modal-total { color: #667eea; font-size: 1.3rem; }
                .box-promo-applied { color: #059669; text-align: right; font-weight: 500; font-size: .98rem; margin-top: 2px;}
                .box-modal-footer { display: flex; flex-wrap: wrap; gap:1.2rem; align-items: flex-end; margin: 1rem 0;}
                .box-promo { flex: 2; min-width: 200px;}
                .box-promo label { font-weight: 500; font-size: 1rem; color: #23232b;}
                .box-promo-inputs { display: flex; gap: 0.65rem; align-items: center; margin-top: 0.2rem;}
                .box-promo-inputs input { padding: 0.4rem 0.75rem; border-radius: 5px; border: 1px solid #d1d5db; font-size: 1rem; min-width: 105px;}
                .box-promo-error { color: #dc2626; font-size: .97rem; margin-top: 3px;}
                .box-promo-success { color: #059669; font-size: .97rem; margin-top: 3px;}
                .box-modal-actions { flex: 1; min-width: 120px; display: flex; flex-direction: column; gap: 0.5rem; align-items: flex-end;}
                .box-empty { text-align: center; padding: 2.5rem 0; }
                .box-empty i { font-size: 3.2rem; color: #cbd5e0; margin-bottom: 1rem; }
                .box-empty h3 { color: #718096; margin-bottom: 0.5rem; }
                @media (max-width: 600px) {
                  .modal-content.large.box-modal-review { max-width: 97vw; min-width: 0; padding: 0; }
                  .modal-header { padding: 1rem; }
                  .box-items-list { margin-bottom: 1rem; padding: 0 1rem; }
                  .box-item-row { flex-direction: column; align-items: flex-start; gap: 0.7rem; }
                  .box-item-info { width: 100%; }
                  .box-item-actions { width: 100%; justify-content: flex-start; flex-wrap: wrap; }
                  .box-modal-summary { padding: 0.9rem 1rem 0.7rem; }
                  .box-modal-total-row { flex-wrap: wrap; gap: 0.5rem; font-size: 1.05rem; }
                  .box-modal-total { font-size: 1.15rem; }
                  .box-modal-footer { flex-direction: column; gap: 1rem; align-items: stretch; padding: 0 1rem; margin: 1rem 0; }
                  .box-promo { min-width: 0; }
                  .box-promo-inputs { flex-wrap: wrap; }
                  .box-promo-inputs input { flex: 1 1 100%; min-width: 0; }
                  .box-promo-inputs button { flex: 1 1 100%; }
                  .box-modal-actions { align-items: stretch; }
                }
              `}</style>
            </>
          )}
        </div>
      </div>
    );
  }

  // MODAL 2: Personal Info Entry & Final Confirmation
  if (showOrderForm) {
    // ... unchanged code for this step ...
    return (
      <div className="modal-overlay" onClick={() => setIsBoxOpen(false)}>
        <div className="modal-content large" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setIsBoxOpen(false)}>
            <i className="fas fa-times"></i>
          </button>
          <form onSubmit={handleOrderSubmit}>
            <div style={{ padding: '0 2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
                <button 
                  type="button"
                  onClick={() => { setShowOrderForm(false); setShowReviewModal(true); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#667eea',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  <i className="fas fa-arrow-left"></i>
                  Retour
                </button>
                <h3 style={{ margin: '0', color: '#374151' }}>
                  Informations de commande
                </h3>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fullName">
                    <i className="fas fa-user"></i>
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={orderForm.fullName}
                    onChange={handleInputChange}
                    required
                    placeholder="Entrez votre nom complet"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">
                    <i className="fas fa-phone"></i>
                    Numéro de téléphone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={orderForm.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="+212 6 12 34 56 78"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address">
                  <i className="fas fa-map-marker-alt"></i>
                  Adresse *
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={orderForm.address}
                  onChange={handleInputChange}
                  required
                  placeholder="Votre adresse complète"
                />
              </div>

              <div style={{ 
                background: '#f8fafc', 
                padding: '1.5rem', 
                borderRadius: '8px', 
                border: '1px solid #e2e8f0',
                marginTop: '2rem' 
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#374151' }}>
                  <i className="fas fa-receipt"></i>
                  Résumé de la commande
                </h4>
                <div>
                  {boxItems.map(item => (
                    <div key={item.id} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem'
                    }}>
                      <span>{item.name} x{item.quantity}</span>
                      <span>{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  ))}
                  {appliedPromo.discount > 0 && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      color: '#059669',
                      fontWeight: '600',
                      fontSize: '1rem',
                      marginTop: '0.5rem'
                    }}>
                      <span>Remise ({appliedPromo.code}) :</span>
                      <span>-{Math.round(appliedPromo.discount * 100)}%</span>
                    </div>
                  )}
                  <div style={{ 
                    borderTop: '1px solid #e2e8f0', 
                    paddingTop: '0.5rem', 
                    marginTop: '0.5rem',
                    display: 'flex', 
                    justifyContent: 'space-between',
                    fontWeight: '700',
                    fontSize: '1.125rem'
                  }}>
                    <span>Total :</span>
                    <span style={{ color: '#667eea' }}>{formatPrice(getDiscountedTotal())}</span>
                  </div>
                </div>
              </div>

              <div style={{ 
                background: '#fef3cd', 
                padding: '1rem', 
                borderRadius: '8px', 
                border: '1px solid #fbbf24',
                marginTop: '1.5rem',
                fontSize: '0.875rem'
              }}>
                <p style={{ margin: '0 0 0.5rem 0' }}>
                  <i className="fas fa-info-circle" style={{ color: '#d97706' }}></i>
                  <strong> Important :</strong>
                </p>
                <ul style={{ margin: '0', paddingLeft: '1.5rem' }}>
                  <li>Nous vous contacterons sous 2 heures pour confirmer votre commande</li>
                  <li>Le paiement sera collecté à la livraison (Paiement à la livraison)</li>
                  <li>La livraison prend généralement 1 à 3 jours ouvrés</li>
                </ul>
              </div>
            </div>
            <div style={{ padding: '1.5rem 2rem 2rem', display: 'flex', gap: '1rem' }}>
              <button 
                type="button" 
                onClick={() => { setShowOrderForm(false); setShowReviewModal(true); }} 
                className="btn btn-outline"
                disabled={isSubmitting}
              >
                <i className="fas fa-arrow-left"></i>
                Retour
              </button>
              <button 
                type="submit" 
                className="btn btn-primary btn-large"
                disabled={isSubmitting}
                style={{ flex: 1 }}
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Commande en cours...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    Valider la commande
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // MAIN MODAL: Cart (no WhatsApp, only "Passer la commande")
  return (
    <div className="modal-overlay" onClick={() => setIsBoxOpen(false)}>
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={() => setIsBoxOpen(false)}>
          <i className="fas fa-times"></i>
        </button>
        
        <div className="modal-header">
          <h2>
            <i className="fas fa-shopping-cart"></i>
            Votre Panier Para
          </h2>
          <p>Vérifiez vos articles sélectionnés et passez votre commande</p>
        </div>

        <div style={{ padding: '0 2rem 2rem' }}>
          {boxItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 0' }}>
              <i className="fas fa-cart-shopping" style={{ fontSize: '4rem', color: '#cbd5e0', marginBottom: '1rem' }}></i>
              <h3 style={{ color: '#718096', marginBottom: '0.5rem' }}>Votre panier est vide</h3>
              <p style={{ color: '#a0aec0' }}>Commencez à ajouter des produits à votre boîte pour passer une commande</p>
              <button 
                onClick={() => { setIsBoxOpen(false); navigate('/products'); }} 
                className="btn btn-primary"
                style={{ marginTop: '1rem' }}
              >
                <i className="fas fa-shopping-bag"></i>
                Continuer mes achats
              </button>
            </div>
          ) : (
            <>
              {/* Liste des articles de la boîte */}
              <div className="box-items">
                {boxItems.map(item => (
                  <div key={item.id} className="box-item" style={{
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'center',
                    padding: '1rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    marginBottom: '1rem'
                  }}>
                    <img 
                      src={item.imageUrl || 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=80&h=80&fit=crop'} 
                      alt={item.name}
                      style={{ 
                        width: '80px', 
                        height: '80px', 
                        objectFit: 'cover', 
                        borderRadius: '8px' 
                      }}
                    />
                    <div style={{ flex: 1 }} className="box-item-details">
                      <h4 style={{ margin: '0 0 0.25rem 0' }}>{item.name}</h4>
                      <p style={{ margin: '0 0 0.5rem 0', color: '#023A1B', fontSize: '0.875rem' }}>
                        {item.shortTitle}
                      </p>
                      <p style={{ margin: '0', fontWeight: '600', color: '#667eea' }}>
                        {formatPrice(item.price)} l'unité
                      </p>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }} className="box-item-controls">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.875rem', color: '#374151' }}>Qté :</label>
                        <select
                          value={item.quantity}
                          onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '0.875rem'
                          }}
                        >
                          {[1,2,3,4,5,6,7,8,9,10].map(num => (
                            <option key={num} value={num}>{num}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div style={{ textAlign: 'right', minWidth: '80px' }}>
                        <p style={{ margin: '0', fontWeight: '700', fontSize: '1rem' }}>
                          {formatPrice(item.price * item.quantity)}
                        </p>
                      </div>
                      
                      <button
                        onClick={() => removeFromBox(item.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          padding: '0.5rem',
                          borderRadius: '4px'
                        }}
                        title="Retirer de la boîte"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Résumé de la boîte */}
              <div style={{ 
                borderTop: '2px solid #e5e7eb', 
                paddingTop: '1.5rem', 
                marginTop: '1.5rem' 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }} className="box-summary-row">
                  <h3 style={{ margin: '0', color: '#374151' }}>
                    Total ({totalItemCount} articles)
                  </h3>
                  <h3 style={{ margin: '0', color: '#667eea', fontSize: '1.5rem' }}>
                    {formatPrice(getTotalPrice())}
                  </h3>
                </div>
                
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }} className="box-summary-actions">
                  <button 
                    onClick={clearBox}
                    className="btn btn-outline"
                    style={{ flex: 1 }}
                  >
                    <i className="fas fa-trash"></i>
                    Vider le panier
                  </button>
                  <button 
                    onClick={handleShowReviewModal}
                    className="btn btn-primary btn-large"
                    style={{ flex: 2 }}
                    tabIndex={totalItemCount < 1 ? -1 : 0}
                    disabled={totalItemCount < 1}
                  >
                    <i className="fas fa-credit-card"></i>
                    Passer la commande
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BoxModal;